import React, { useEffect } from "react";
import { useUser, useClerk } from "@clerk/clerk-react";
import { useDispatch } from "react-redux";
import { setAuth, clearAuth } from "../../store/slices/authentication";
import useIdleTimeout from "../../hooks/useIdleTimeout";
import IdleWarningModal from "./IdleWarningModal";

// A signed-in account that isn't yet linked to a person on the tree
// (no externalId). Show a calm notice instead of crashing to a blank page.
function AccountSetupPending({ onSignOut }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        background: "#f8f5eb",
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
      }}
    >
      <div
        style={{
          maxWidth: "480px",
          textAlign: "center",
          background: "#ffffff",
          border: "1px solid #e4dfd2",
          borderRadius: "14px",
          padding: "32px 28px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
        }}
      >
        <div style={{ fontSize: "44px", marginBottom: "10px" }}>🌳</div>
        <h1
          style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            color: "#1f2a44",
            fontSize: "24px",
            margin: "0 0 10px",
          }}
        >
          Welcome — we're finishing your setup
        </h1>
        <p style={{ color: "#444", fontSize: "16px", lineHeight: 1.55, margin: "0 0 22px" }}>
          Your account is signed in, but it isn't linked to your place on the
          family tree yet. This is usually sorted out within a day. Please check
          back soon, or reply to your invitation email for help.
        </p>
        <button
          onClick={onSignOut}
          style={{
            background: "#1f2a44",
            color: "#ffffff",
            border: "none",
            borderRadius: "10px",
            padding: "11px 24px",
            fontSize: "15px",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

function AuthWrapper({ children }) {
  const dispatch = useDispatch();
  const { isLoaded, isSignedIn, user } = useUser();
  const { signOut } = useClerk();
  const { showWarning, secondsLeft, stayActive } = useIdleTimeout(
    Boolean(isLoaded && isSignedIn)
  );

  // Signed in, but the account isn't linked to a tree person yet.
  const setupPending = Boolean(isLoaded && isSignedIn && user && !user.externalId);

  useEffect(() => {
    if (!isLoaded) return;

    if (isSignedIn && user && user.externalId) {
      const auth_user = {
        id: user.id,
        externalId: user.externalId,
        // Fall back gracefully so a missing username never blanks the app.
        username:
          user.username ||
          user.primaryEmailAddress?.emailAddress ||
          user.id,
        emailAddress:
          user.primaryEmailAddress?.emailAddress ||
          user.emailAddresses?.[0]?.emailAddress ||
          "",
        imageUrl: user.imageUrl,
      };
      dispatch(setAuth({ user: auth_user }));
    } else {
      // Not signed in, or signed in but not yet linked — don't authenticate.
      if (isSignedIn && user && !user.externalId) {
        // eslint-disable-next-line no-console
        console.warn("Signed-in user has no externalId; account link pending.");
      }
      dispatch(clearAuth());
    }
  }, [isLoaded, isSignedIn, user, dispatch]);

  return (
    <>
      {setupPending ? (
        <AccountSetupPending onSignOut={() => signOut()} />
      ) : (
        children
      )}
      {showWarning && (
        <IdleWarningModal secondsLeft={secondsLeft} onStayActive={stayActive} />
      )}
    </>
  );
}

export default AuthWrapper;
